/**
 * Loader component
 */
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChange,
} from '@angular/core';
import { HIDE_LOADER_DELAY } from '@config/global-constants';
import { UiActions } from '@store/actions/ui/ui.actions';
import {
  observableOf,
  observableTimer,
  rx,
  Subject,
  Subscription,
} from '@shared/rx';
import { ReplaySubject } from 'rxjs';
import { StateService } from '@app/shared/services';
import { IProgressBarLoader } from '@app/store/reducers/ui';
import { calculatePercentage } from '@app/shared/utils';
import { decimalNumber } from '@app/shared/common';

@Component({
  selector: 'xux-loader',
  templateUrl: './xux-loader.component.html',
  styleUrls: ['./xux-loader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XUXLoaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public isVisible: boolean;
  @Input() public params: IProgressBarLoader = {
    showBar: null,
    total: null,
    current: null,
    text: null,
  };

  public showProgressBar: boolean = false;
  public text: string = '';
  public percentage: number = 0;

  private isVisible$: Subject<boolean> = new ReplaySubject<boolean>(1);
  private subs: Subscription[] = [];

  public constructor(
    public uiActions: UiActions,
    private stateService: StateService,
  ) {}

  public ngOnInit(): void {
    this.subs.push(
      this.isVisible$
        .pipe(
          rx.switchMap((isVisible) =>
            isVisible
              ? observableTimer(HIDE_LOADER_DELAY)
              : observableOf(false),
          ),
        )
        .subscribe(() => {
          const { isBatchRequestLoading } = this.stateService.getState().ui;
          if (!isBatchRequestLoading) {
            this.uiActions.disableLoader();
          }
        }),
    );
  }

  public ngOnChanges(
    changes: Record<keyof XUXLoaderComponent, SimpleChange>,
  ): void {
    if (changes.params) {
      const {
        showBar = false,
        total = 0,
        current = 0,
        text = '',
      } = changes.params.currentValue || {};
      this.showProgressBar = showBar;
      this.percentage = decimalNumber(calculatePercentage(current, total))
        .mul(100)
        .valueOf();
      this.text = text;
    }
    if (changes.isVisible) this.isVisible$.next(this.isVisible);
  }

  public ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  public close(): void {
    this.isVisible$.next(false);
  }
}
